import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'

export interface AppDeviceInfo {
  platform: 'ios' | 'android' | 'web'
  displayMode: 'ios' | 'android' | 'twa' | 'pwa' | 'browser'
  appVersion: string
  appBuild: string
  appId: string
  appName: string
  osVersion: string
  deviceModel: string
  manufacturer: string
  isVirtual: boolean
  webViewVersion: string
  preferredLanguage: string
  screenWidth: number
  screenHeight: number
  timezone: string
  pushPermission: 'granted' | 'denied' | 'default' | 'provisional' | 'ephemeral' | 'unsupported'
}

const DISPLAY_MODE_KEY = 'cc_display_mode'
const PUSH_PERMISSION_KEY = 'cc_push_permission'

function detectDisplayMode (): AppDeviceInfo['displayMode'] {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android'
  }

  // Check persisted mode first — ensures TWA is identified correctly after SPA navigation
  // or hard refresh (document.referrer only contains android-app:// on the very first load)
  const persisted = sessionStorage.getItem(DISPLAY_MODE_KEY) as AppDeviceInfo['displayMode'] | null
  if (persisted === 'twa' || persisted === 'pwa') {
    return persisted
  }

  // TWA: referrer is only reliable on the initial launch, so persist immediately
  if (document?.referrer?.startsWith('android-app://com.compareclub.app')) {
    sessionStorage.setItem(DISPLAY_MODE_KEY, 'twa')
    return 'twa'
  }

  // TWA: URL params or sessionStorage (persisted by inline head script) are reliable signals
  if (new URLSearchParams(window.location.search).has('_twa_version') || sessionStorage.getItem('_twa_version')) {
    sessionStorage.setItem(DISPLAY_MODE_KEY, 'twa')
    return 'twa'
  }

  // TWA also matches standalone mode, but we've already returned above if persisted === 'twa'.
  // Reaching here means no TWA signals were detected this session, so standalone = PWA.
  if (window.matchMedia('(display-mode: standalone)').matches) {
    sessionStorage.setItem(DISPLAY_MODE_KEY, 'pwa')
    return 'pwa'
  }

  return 'browser'
}

const TWA_PARAM_KEYS = ['_twa_version', '_twa_build', '_twa_model', '_twa_os', '_twa_manufacturer', '_twa_push_permission'] as const

function parseTwaParamsFromUrl (): Partial<AppDeviceInfo> {
  const params = new URLSearchParams(window.location.search)
  const info: Partial<AppDeviceInfo> = {}

  // Read from URL params first, fall back to sessionStorage (persisted by
  // 00.twa-params.global middleware before route redirects can strip the params)
  const get = (key: string): string | null => params.get(key) || sessionStorage.getItem(key)

  const version = get('_twa_version')
  if (version) info.appVersion = version
  const build = get('_twa_build')
  if (build) info.appBuild = build
  const model = get('_twa_model')
  if (model) info.deviceModel = model
  const os = get('_twa_os')
  if (os) info.osVersion = os
  const manufacturer = get('_twa_manufacturer')
  if (manufacturer) info.manufacturer = manufacturer
  const pushPerm = get('_twa_push_permission')
  if (pushPerm) {
    info.pushPermission = pushPerm as AppDeviceInfo['pushPermission']
    sessionStorage.setItem(PUSH_PERMISSION_KEY, info.pushPermission)
  }

  // Clean TWA params from URL to avoid leaking into analytics/sharing
  if (params.has('_twa_version')) {
    const url = new URL(window.location.href)
    TWA_PARAM_KEYS.forEach(p => url.searchParams.delete(p))
    window.history.replaceState({}, '', url.pathname + url.search + url.hash)
  }

  return info
}

function parseUserAgent (): Partial<AppDeviceInfo> {
  const ua = navigator.userAgent
  const info: Partial<AppDeviceInfo> = {}

  // Android: "Linux; Android 14; Pixel 7"
  const androidMatch = ua.match(/Android\s([\d.]+)(?:;[^;]*;\s*(.+?)\s*(?:Build|[)]))?/i)
  if (androidMatch) {
    info.osVersion = androidMatch[1]
    if (androidMatch[2]) info.deviceModel = androidMatch[2]
  }

  // iOS: "iPhone OS 17_4 like Mac OS X"
  const iosMatch = ua.match(/(?:iPhone|iPad) OS (\d+[_\d]*)/i)
  if (iosMatch) {
    info.osVersion = iosMatch[1].replace(/_/g, '.')
  }

  return info
}

export default defineNuxtPlugin({
  name: 'app-device-info',
  dependsOn: ['braze-bridge'],
  async setup () {
    const displayMode = detectDisplayMode()
    const platform = Capacitor.getPlatform()

    // Defaults — pushPermission set below per platform
    const deviceInfo: AppDeviceInfo = {
      platform: platform === 'ios' || platform === 'android' ? platform : 'web',
      displayMode,
      appVersion: '',
      appBuild: '',
      appId: '',
      appName: '',
      osVersion: '',
      deviceModel: '',
      manufacturer: '',
      isVirtual: false,
      webViewVersion: '',
      preferredLanguage: navigator.language || '',
      screenWidth: window.screen?.width || 0,
      screenHeight: window.screen?.height || 0,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      pushPermission: 'default'
    }

    if (Capacitor.isNativePlatform()) {
    // ── iOS / Android Capacitor ──
      try {
        const [appInfo, devInfo] = await Promise.all([
          App.getInfo(),
          Device.getInfo()
        ])
        deviceInfo.appVersion = appInfo.version
        deviceInfo.appBuild = appInfo.build
        deviceInfo.appId = appInfo.id
        deviceInfo.appName = appInfo.name
        deviceInfo.osVersion = devInfo.osVersion
        deviceInfo.deviceModel = devInfo.model
        deviceInfo.manufacturer = devInfo.manufacturer
        deviceInfo.isVirtual = devInfo.isVirtual
        deviceInfo.webViewVersion = devInfo.webViewVersion
      } catch (e) {
      // eslint-disable-next-line no-console
        console.error('[AppDeviceInfo] Failed to get native device info', e)
      }

      // Check native push permission via the existing $brazeBridge plugin
      try {
        const { $brazeBridge } = useNuxtApp()
        if ($brazeBridge) {
          const result = await $brazeBridge.checkPushPermission()
          deviceInfo.pushPermission = result.permission as AppDeviceInfo['pushPermission']
        }
      } catch (e) {
      // eslint-disable-next-line no-console
        console.warn('[AppDeviceInfo] Failed to check native push permission', e)
      }
    } else if (displayMode === 'twa') {
    // ── Android TWA ──
    // 1. Read URL params (fallback, always available)
      const twaParams = parseTwaParamsFromUrl()
      Object.assign(deviceInfo, twaParams)
      deviceInfo.appId = 'com.compareclub.app'
      deviceInfo.platform = 'android'

      // 2. Listen for PostMessage from native (Chrome 115+)
      window.addEventListener('message', event => {
        try {
          const data = typeof event.data === 'string'
            ? JSON.parse(event.data)
            : event.data
          if (data?.type === 'twa_device_info') {
            deviceInfo.appVersion = data.appVersion || deviceInfo.appVersion
            deviceInfo.appBuild = String(data.appBuild || deviceInfo.appBuild)
            deviceInfo.appId = data.packageId || deviceInfo.appId
            deviceInfo.deviceModel = data.deviceModel || deviceInfo.deviceModel
            deviceInfo.osVersion = data.osVersion || deviceInfo.osVersion
            deviceInfo.manufacturer = data.manufacturer || deviceInfo.manufacturer
            // eslint-disable-next-line no-console
            console.log('[AppDeviceInfo] Updated from PostMessage', deviceInfo)
          }
        } catch {
        /* ignore non-JSON messages */
        }
      })

      // 3. Supplement with UA parsing if URL params were missing
      if (!deviceInfo.osVersion) {
        const uaInfo = parseUserAgent()
        Object.assign(deviceInfo, { ...uaInfo, ...twaParams }) // twaParams take priority
      }
    } else {
    // ── PWA / Web ──
      const uaInfo = parseUserAgent()
      Object.assign(deviceInfo, uaInfo)
    }

    // For non-native platforms, use the web Notification API for push permission
    if (!Capacitor.isNativePlatform()) {
      if (displayMode === 'twa') {
        // TWA: prefer native permission from URL param (already set via parseTwaParamsFromUrl),
        // fall back to sessionStorage persisted value from a previous hash callback
        const persisted = sessionStorage.getItem(PUSH_PERMISSION_KEY) as AppDeviceInfo['pushPermission'] | null
        if (!deviceInfo.pushPermission || deviceInfo.pushPermission === 'default') {
          deviceInfo.pushPermission = persisted || 'default'
        }
      } else {
        deviceInfo.pushPermission = typeof Notification !== 'undefined'
          ? Notification.permission as AppDeviceInfo['pushPermission']
          : 'unsupported'
      }
    }

    // eslint-disable-next-line no-console
    console.log('[AppDeviceInfo]', displayMode, deviceInfo)

    return {
      provide: {
        appDeviceInfo: deviceInfo
      }
    }
  }
})
